import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  LandPlot,
  Factory,
  Users,
  Layers,
  Ruler,
  ShieldCheck,
  Zap,
  Truck,
  TreePine,
  MapPin,
  Maximize2,
  X,
  ChevronLeft,
  ChevronRight,
  Building2,
} from 'lucide-react';

/* ─────────────────────────────  CountUp  ───────────────────────────── */
const CountUp = ({ end, duration = 2000, decimals = 0, separator = '.' }) => {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !startedRef.current) {
          startedRef.current = true;
          const startTime = performance.now();
          const tick = (now) => {
            const progress = Math.min((now - startTime) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setValue(end * eased);
            if (progress < 1) requestAnimationFrame(tick);
            else setValue(end);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [end, duration]);

  const formatted = Number(value).toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  return <span ref={ref}>{formatted}</span>;
};

/* ─────────────────────────────  Data  ───────────────────────────── */
const stats = [
  { icon: LandPlot, value: 434000, suffix: 'm²', label: 'Toplam Proje Alanı', color: 'from-blue-500 to-blue-700', glow: 'bg-blue-500/20' },
  { icon: Factory, value: 434, suffix: '', label: 'Birimlik Sanayi İş Yeri', color: 'from-emerald-500 to-emerald-700', glow: 'bg-emerald-500/20' },
  { icon: Users, value: 4000, suffix: 'm²', label: 'Sosyal Tesis Binası', color: 'from-amber-500 to-amber-700', glow: 'bg-amber-500/20' },
  { icon: Layers, value: 2, suffix: '', label: 'Etap Yapılaşma', color: 'from-purple-500 to-purple-700', glow: 'bg-purple-500/20' },
];

const features = [
  { icon: Ruler, title: 'Esnek Metrekare Seçenekleri', desc: 'Üreticilerin farklı ölçek ihtiyaçlarına uygun, küçük atölyeden büyük üretim alanına kadar değişen iş yeri tipleri.' },
  { icon: ShieldCheck, title: 'Güçlü Altyapı', desc: 'Yangın hidrant sistemi, kapalı CCTV, çevre güvenliği ve modern aydınlatma standartlarıyla donatılmış komple altyapı.' },
  { icon: Zap, title: 'Yüksek Kapasiteli Elektrik', desc: 'Sanayi tipi üretim için yeterli enerji altyapısı, jeneratör entegrasyonu ve kesintisiz enerji desteği.' },
  { icon: Truck, title: 'Lojistik Avantajı', desc: 'İstanbul Çorlu yolu üzerinde, TEM ve D-100 bağlantılarına dakikalar mesafede stratejik konum.' },
  { icon: TreePine, title: 'Yeşil & Sosyal Alanlar', desc: '4.000 m² sosyal tesis binası; yemekhane, dinlenme alanları ve peyzajlı ortak alanlar.' },
  { icon: MapPin, title: 'Silivri\'nin Merkezinde', desc: 'Gümüşyaka\'da, hem Avrupa Yakası iş çevrelerine hem de liman/havaalanlarına kolay erişim.' },
];

const krokiImages = [
  { src: '/images/krokiler/kroki-renkli.jpg', label: 'Vaziyet Planı (Blok Ayrımı)' },
  { src: '/images/krokiler/kroki-detay.jpg', label: 'Vaziyet Planı (Detaylı)' },
];

const galleryImages = [
  '/images/renders/render1.jpg',
  '/images/renders/render2.jpg',
  '/images/renders/render3.jpg',
  '/images/renders/render4.jpg',
  '/images/renders/render5.jpg',
];

/* ─────────────────────────────  Page  ───────────────────────────── */
const ProjectDetail = () => {
  const [lightbox, setLightbox] = useState(null); // {list, index}

  const openLightbox = (list, index) => setLightbox({ list, index });
  const closeLightbox = () => setLightbox(null);
  const prev = (e) => {
    e?.stopPropagation();
    setLightbox((l) => l && { ...l, index: (l.index - 1 + l.list.length) % l.list.length });
  };
  const next = (e) => {
    e?.stopPropagation();
    setLightbox((l) => l && { ...l, index: (l.index + 1) % l.list.length });
  };

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  return (
    <div className="bg-slate-50">
      {/* HERO */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=1920&q=80"
            alt="Proje"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-slate-900/60"></div>
        </div>
        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 text-blue-300 text-sm font-semibold mb-4">
              <Link to="/" className="hover:text-white transition-colors">Anasayfa</Link>
              <ChevronRight size={14} />
              <span className="text-white">Proje Detayı</span>
            </div>
            <span className="inline-block px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 border border-blue-400/30 text-xs font-bold uppercase tracking-wider mb-5 backdrop-blur-sm">
              Trakya'nın En Büyük Karma Sanayi Sitesi
            </span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white tracking-tight leading-[1.05] mb-6 max-w-4xl">
              S.S. İkitelli – Silivri Toplu İşyeri Yapı Kooperatifi
            </h1>
            <p className="text-lg md:text-xl text-slate-200 max-w-3xl leading-relaxed">
              Silivri Gümüşyaka'da 434.000 m² alan üzerinde inşa edilen, 434 sanayi iş yeri ve 4.000 m² sosyal tesisten oluşan modern karma sanayi sitesi projesi.
            </p>
          </motion.div>
        </div>
      </section>

      {/* RAKAMLARLA PROJE */}
      <section className="py-20 md:py-24 -mt-12 relative z-20">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {stats.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="group relative bg-white rounded-3xl p-6 md:p-8 shadow-xl shadow-slate-200/60 border border-slate-100 hover:-translate-y-1 transition-all duration-500"
                >
                  <div className={`absolute -top-2 -right-2 w-20 h-20 ${s.glow} blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity`} />
                  <div className={`inline-flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br ${s.color} text-white shadow-lg mb-4 relative`}>
                    <Icon size={24} />
                  </div>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-3xl md:text-5xl font-extrabold bg-gradient-to-br from-slate-900 to-slate-700 bg-clip-text text-transparent tabular-nums">
                      <CountUp end={s.value} />
                    </span>
                    {s.suffix && <span className="text-base md:text-xl font-bold text-slate-400">{s.suffix}</span>}
                  </div>
                  <p className="text-xs md:text-sm font-semibold text-slate-500 uppercase tracking-wider">{s.label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROJE TANITIMI */}
      <section className="py-20 md:py-24">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-300/40 aspect-[4/3]"
            >
              <img src="/images/renders/render2.jpg" alt="Proje Görseli" className="w-full h-full object-cover" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/80 to-transparent p-8">
                <span className="text-white font-semibold">3D Görselleştirme</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <h4 className="text-blue-600 font-semibold uppercase tracking-[0.2em] text-xs mb-3">Projeye Genel Bakış</h4>
              <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight mb-6">
                Modern Üretim Alanlarının <span className="text-blue-600">Yeni Adresi</span>
              </h2>
              <div className="space-y-5 text-slate-600 leading-relaxed text-base md:text-lg">
                <p>
                  <strong className="text-slate-900">S.S. İkitelli – Silivri Toplu İşyeri Yapı Kooperatifi</strong>,
                  bölgede üretim ve ticaret yapan iş insanlarının modern, planlı ve güvenli bir çalışma ortamına
                  duyduğu ihtiyaçtan doğmuştur. Silivri Gümüşyaka'da 434.000 m² lik proje alanımız üzerinde
                  yükselen sanayi sitemiz, üyelerimize sadece bir iş yeri değil; <strong>geleceğe yatırım</strong>{' '}
                  imkânı sunmaktadır.
                </p>
                <p>
                  Proje kapsamında <strong>434 adet sanayi iş yeri</strong> ve <strong>4.000 m² sosyal tesis binası</strong> yer
                  almakta; üretim alanları farklı ölçek ve ihtiyaçlara cevap verecek şekilde A–N blokları halinde
                  modüler olarak tasarlanmıştır.
                </p>
                <p>
                  Yüksek tavan yükseklikleri, geniş manevra alanları, modern altyapı sistemleri ve sosyal donatılarıyla
                  proje, bölgenin <strong>en kapsamlı karma sanayi yatırımı</strong> olarak öne çıkmaktadır.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/iletisim"
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold flex items-center gap-2 transition-all shadow-lg shadow-blue-600/20 hover:-translate-y-0.5"
                >
                  Bilgi Al <ArrowRight size={18} />
                </Link>
                <Link
                  to="/galeri"
                  className="px-6 py-3 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-xl font-semibold flex items-center gap-2 transition-all"
                >
                  Galeriyi Gör
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ÖZELLİKLER */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container mx-auto px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto mb-16"
          >
            <h4 className="text-blue-600 font-semibold uppercase tracking-[0.2em] text-xs mb-3">Proje Özellikleri</h4>
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tight">
              Üretime Değer Katan <span className="text-blue-600">Detaylar</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="group p-7 rounded-2xl border border-slate-200 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-100/30 transition-all duration-500 hover:-translate-y-1 bg-gradient-to-br from-white to-slate-50"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-50 text-blue-600 mb-5 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                    <Icon size={22} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* KROKİ / VAZİYET PLANI */}
      <section className="py-20 md:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto mb-12"
          >
            <h4 className="text-blue-600 font-semibold uppercase tracking-[0.2em] text-xs mb-3">Yerleşim Planı</h4>
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tight">
              Vaziyet Planı & <span className="text-blue-600">Krokiler</span>
            </h2>
            <p className="text-slate-500 mt-4">
              İki ayrı parsel üzerinde A'dan N'ye kadar bloklar halinde planlanmış üretim alanları.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {krokiImages.map((k, i) => (
              <motion.div
                key={k.src}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group relative bg-white rounded-3xl overflow-hidden shadow-xl shadow-slate-200/60 border border-slate-100 cursor-zoom-in"
                onClick={() => openLightbox(krokiImages.map(x => x.src), i)}
              >
                <div className="aspect-[3/2] overflow-hidden bg-slate-100 relative">
                  <img
                    src={k.src}
                    alt={k.label}
                    className="w-full h-full object-contain p-4 group-hover:scale-[1.03] transition-transform duration-700"
                    onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
                  />
                  <div className="absolute inset-0 hidden flex-col items-center justify-center text-center p-8 text-slate-400">
                    <Building2 size={48} strokeWidth={1.5} className="mb-3" />
                    <p className="text-sm font-medium">Kroki yüklenecek</p>
                    <p className="text-xs mt-1 opacity-70">{k.src}</p>
                  </div>
                  <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md">
                    <Maximize2 size={18} className="text-slate-700" />
                  </div>
                </div>
                <div className="p-5 border-t border-slate-100 flex items-center justify-between">
                  <span className="font-semibold text-slate-900 text-sm md:text-base">{k.label}</span>
                  <span className="text-xs text-slate-500 font-medium">Büyütmek için tıklayın</span>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 p-6 rounded-2xl bg-blue-50/60 border border-blue-100 text-sm text-blue-900 text-center">
            <strong>Not:</strong> Detaylı kat planları, parsel bilgileri ve fiyat listesi için lütfen{' '}
            <Link to="/iletisim" className="underline font-semibold hover:text-blue-700">iletişim sayfamızdan</Link>{' '}
            bizimle iletişime geçiniz.
          </div>
        </div>
      </section>

      {/* GÖRSEL GALERİ */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container mx-auto px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto mb-12"
          >
            <h4 className="text-blue-600 font-semibold uppercase tracking-[0.2em] text-xs mb-3">3D Görselleştirmeler</h4>
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tight">
              Projeden <span className="text-blue-600">Kareler</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {galleryImages.map((src, i) => (
              <button
                key={src}
                onClick={() => openLightbox(galleryImages, i)}
                className="relative overflow-hidden rounded-2xl bg-slate-100 aspect-square group focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <img
                  src={src}
                  alt={`Görsel ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/30 transition-colors" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900"></div>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(59,130,246,0.5), transparent 50%), radial-gradient(circle at 80% 50%, rgba(16,185,129,0.4), transparent 50%)' }}></div>
        <div className="container mx-auto px-6 max-w-4xl relative z-10 text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight leading-tight mb-6">
            Geleceğin İş Merkezinde <br/> Yerinizi Alın
          </h2>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            Kooperatif üyeliği, parsel bilgisi ve detaylı sunum için bizimle iletişime geçin.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/iletisim" className="px-8 py-4 bg-white text-slate-900 hover:bg-blue-50 rounded-xl font-bold flex items-center gap-2 transition-all shadow-2xl hover:-translate-y-0.5">
              İletişime Geç <ArrowRight size={18} />
            </Link>
            <Link to="/haberler" className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold border border-white/20 backdrop-blur transition-all">
              Güncel Haberler
            </Link>
          </div>
        </div>
      </section>

      {/* LIGHTBOX */}
      {lightbox && (
        <div onClick={closeLightbox} className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4">
          <button onClick={closeLightbox} className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition" aria-label="Kapat">
            <X size={24} />
          </button>
          {lightbox.list.length > 1 && (
            <>
              <button onClick={prev} className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition">
                <ChevronLeft size={28} />
              </button>
              <button onClick={next} className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition">
                <ChevronRight size={28} />
              </button>
            </>
          )}
          <img
            src={lightbox.list[lightbox.index]}
            alt=""
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
          />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-white/10 text-white text-sm font-medium">
            {lightbox.index + 1} / {lightbox.list.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;
